<script lang="ts" setup>
  import {
    Button,
    ButtonColor,
    ButtonSize,
    ButtonVariant,
    Dropdown,
    DropdownPlacement,
    FontIcon,
    Tooltip,
    TooltipPosition,
  } from '@tundr/ds-components';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed } from 'vue';
  import { useSidebarStore } from '../../store/sidebar.store';
  import { SidebarVariant } from '../Sidebar/Sidebar.types';
  import {
    collapsedPopoverTriggerClass,
    collapsedTriggerClass,
    menuAreaCollapsedClass,
    menuAreaExpandedClass,
    menuContentClass,
    menuTitleClass,
    menuTitleNameClass,
    userNameBoxClass,
    userNameClass,
    userNameLabelClass,
  } from './SidebarUserMenu.css';
  import type { SidebarUserMenuProps } from './SidebarUserMenu.types';

  const props = defineProps<SidebarUserMenuProps>();

  const sidebarStore = useSidebarStore();

  const triggerColor = computed(() =>
    (props.variant ?? sidebarStore.variant) === SidebarVariant.OnBrand
      ? ButtonColor.OnBrand
      : undefined,
  );

  const collapsedTooltipTitle = computed(
    () => props.userName || props.triggerAriaLabel,
  );
</script>

<template>
  <div :class="userNameBoxClass">
    <div v-if="!collapsed" :class="userNameClass">
      <FontIcon :icon-name="TUNDR_ICONS.AVATAR" />
      <Tooltip
        :title="userName || undefined"
        :position="TooltipPosition.RIGHT"
        :interactive="false"
      >
        <div :class="userNameLabelClass">{{ userName }}</div>
      </Tooltip>
    </div>

    <div :class="collapsed ? menuAreaCollapsedClass : menuAreaExpandedClass">
      <Dropdown
        :placement="DropdownPlacement.BOTTOM_END"
        :trigger-as-child="!collapsed"
        :trigger-full-width="collapsed"
        :slot-trigger-class="collapsed ? collapsedPopoverTriggerClass : undefined"
        :trigger-aria-label="triggerAriaLabel"
      >
        <template #trigger>
          <Tooltip
            v-if="collapsed"
            :title="collapsedTooltipTitle"
            :position="TooltipPosition.RIGHT"
            :interactive="false"
          >
            <div :class="collapsedTriggerClass">
              <FontIcon
                :icon-name="TUNDR_ICONS.AVATAR"
                test-id="user-menu-trigger"
              />
            </div>
          </Tooltip>
          <Button
            v-else
            :variant="ButtonVariant.Icon"
            :color="triggerColor"
            is-ghost
            :icon-name="TUNDR_ICONS.OPTIONS"
            :size="ButtonSize.SM"
            test-id="user-menu-trigger"
          />
        </template>

        <div :class="menuContentClass">
          <div :class="menuTitleClass">
            <span :class="menuTitleNameClass">{{ userName }}</span>
            <slot name="badge" />
          </div>
          <slot />
        </div>
      </Dropdown>
    </div>
  </div>
</template>
